import React, { useState, useEffect } from 'react';
import { Save, Palette, Check, Eye, X, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { UpdateVfxConfigRequest } from '../../../types';
import { useUpdateVfxConfig } from '../../../hooks/useApi';
import { APP_THEMES } from '../../../constants/appThemes';

interface AppThemeConfigFormProps {
  appTheme: number | null;
  onSuccess?: () => void;
  scheduleId?: string;
  onScheduleUpdate?: (data: UpdateVfxConfigRequest) => Promise<void>;
  /** When saving via parent (e.g. schedule), pass true while API is in progress so button shows loading */
  externalSaving?: boolean;
}

const AppThemeConfigForm: React.FC<AppThemeConfigFormProps> = ({ appTheme, onSuccess, scheduleId, onScheduleUpdate, externalSaving = false }) => {
  const [selectedTheme, setSelectedTheme] = useState<number | null>(null);
  const [previewTheme, setPreviewTheme] = useState<number | null>(null);

  const updateConfig = useUpdateVfxConfig();

  useEffect(() => {
    if (appTheme !== undefined && appTheme !== null) {
      // Handle both string and number types
      const themeValue = typeof appTheme === 'string' ? parseInt(appTheme, 10) : appTheme;
      setSelectedTheme(!isNaN(themeValue) ? themeValue : null);
    } else {
      setSelectedTheme(null);
    }
  }, [appTheme]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateData: UpdateVfxConfigRequest = {
      app_themes: selectedTheme || null,
    };

    try {
      if (scheduleId && onScheduleUpdate) {
        await onScheduleUpdate(updateData);
      } else {
      await updateConfig.mutateAsync(updateData);
      }
      onSuccess?.();
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleThemeSelect = (themeId: number) => {
    setSelectedTheme(selectedTheme === themeId ? null : themeId);
  };

  const getVfxCompatibleBadge = (compatible: string) => {
    if (compatible === 'excellent') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <Star className="h-3 w-3 mr-1" />
          Excellent
        </span>
      );
    } else if (compatible === 'good') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Good
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        Fair
      </span>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Palette className="h-5 w-5 mr-2" />
          App Theme Configuration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Theme Selection */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Select App Theme
            </label>
            <p className="text-xs text-muted-foreground mb-4">
              Choose a theme for the application interface. Themes affect the overall color scheme and gradient backgrounds.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {APP_THEMES.map((theme) => {
                const isSelected = selectedTheme === theme.id;
                return (
                  <div
                    key={theme.id}
                    className={`relative group border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                      isSelected
                        ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => handleThemeSelect(theme.id)}
                  >
                    {/* Gradient Preview */}
                    <div className={`aspect-video ${theme.gradient} relative`}>
                      {isSelected && (
                        <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center pointer-events-none">
                          <Check className="h-8 w-8 text-blue-600" />
                        </div>
                      )}
                      {/* Preview Button - Top Right Corner */}
                      <div className="absolute top-2 right-2 z-30">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setPreviewTheme(theme.id);
                          }}
                          className="bg-white/90 hover:bg-white shadow-md"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Theme Info */}
                    <div className="p-3 bg-white">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold">{theme.name}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {theme.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        {getVfxCompatibleBadge(theme.vfxCompatible)}
                        <span className="text-xs text-muted-foreground">
                          #{theme.id}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                        {theme.bestFor}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedTheme === null && (
              <p className="text-xs text-muted-foreground mt-3">
                No theme selected. Click on a theme card to select it.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              disabled={updateConfig.isLoading || externalSaving}
              className="flex items-center space-x-2"
            >
              {updateConfig.isLoading || externalSaving ? (
                <>
                  <LoadingSpinner />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save App Theme Configuration</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>

      {/* Preview Modal */}
      {previewTheme !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-75 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6"
          onClick={() => setPreviewTheme(null)}
        >
          <div className="min-h-full flex items-start justify-center">
            <div className="relative w-full max-w-4xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-xl font-semibold">
                    {APP_THEMES.find(t => t.id === previewTheme)?.name || `Theme ${previewTheme}`}
                  </CardTitle>
                  {APP_THEMES.find(t => t.id === previewTheme)?.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {APP_THEMES.find(t => t.id === previewTheme)?.description}
                    </p>
                  )}
                  <div className="mt-2">
                    {APP_THEMES.find(t => t.id === previewTheme) && getVfxCompatibleBadge(APP_THEMES.find(t => t.id === previewTheme)!.vfxCompatible)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewTheme(null)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className={`relative w-full rounded-lg border overflow-hidden ${APP_THEMES.find(t => t.id === previewTheme)?.gradient || ''}`} style={{ aspectRatio: '16/9' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white/30 text-2xl font-medium">Theme Preview</span>
                  </div>
                </div>
                {APP_THEMES.find(t => t.id === previewTheme) && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Best For:</p>
                    <p className="text-sm text-muted-foreground">
                      {APP_THEMES.find(t => t.id === previewTheme)?.bestFor}
                    </p>
                  </div>
                )}
                <div className="flex justify-end mt-4 space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewTheme(null)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      handleThemeSelect(previewTheme);
                      setPreviewTheme(null);
                    }}
                  >
                    Select This Theme
                  </Button>
                </div>
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AppThemeConfigForm;

